import { format } from 'date-fns'

export const allFields = (form) => (form?.sections || []).flatMap((s) => s.fields)

/** Field default (always a string in the form definition) → the value an input works with. */
export const defaultFor = (field) => {
  const raw = field.defaultValue
  if (raw === null || raw === undefined || raw === '') return field.fieldType === 'MULTI_SELECT' ? [] : undefined
  switch (field.fieldType) {
    case 'CHECKBOX':
      return raw === 'true'
    case 'MULTI_SELECT':
      return raw.split(',').map((v) => v.trim()).filter(Boolean)
    default:
      return raw
  }
}

export const initialValues = (form) => {
  const values = {}
  allFields(form).forEach((field) => {
    const value = defaultFor(field)
    if (value !== undefined) values[field.fieldKey] = value
  })
  return values
}

/** Server data → input values (numbers become strings so text inputs can edit them freely). */
export const toInputValues = (form, data) => {
  const values = {}
  allFields(form).forEach((field) => {
    const value = data?.[field.fieldKey]
    if (value === undefined || value === null) return
    values[field.fieldKey] = typeof value === 'number' ? String(value) : value
  })
  return values
}

/**
 * Input values → request data. Empty values are left out (the backend treats missing as empty);
 * read-only fields are left out (the backend owns them). Numbers stay strings: the backend parses
 * them exactly, which avoids float rounding in the browser.
 */
export const toRequestData = (form, values) => {
  const data = {}
  allFields(form).forEach((field) => {
    if (field.readOnly || field.fieldType === 'ROLLUP') return
    const value = values[field.fieldKey]
    if (value === undefined || value === null || value === '') return
    if (Array.isArray(value) && value.length === 0) return
    data[field.fieldKey] = typeof value === 'string' && field.fieldType !== 'TEXTAREA' ? value.trim() : value
  })
  return data
}

const optionLabel = (field, value) => field.options.find((o) => o.value === value)?.label ?? value

/** Read-only display text for lists and summaries. */
export const formatValue = (field, value) => {
  if (value === undefined || value === null || value === '') return '—'
  switch (field.fieldType) {
    case 'CHECKBOX':
      return value ? 'Yes' : 'No'
    case 'SELECT':
    case 'RADIO':
      return optionLabel(field, value)
    case 'MULTI_SELECT':
      return (Array.isArray(value) ? value : [value]).map((v) => optionLabel(field, v)).join(', ')
    case 'CURRENCY':
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    case 'DECIMAL':
    case 'NUMBER':
    case 'ROLLUP':
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 4 })
    case 'LOOKUP':
    case 'USER':
      return String(value) // lists render LookupLink from record.lookups instead
    case 'DATE':
      try { return format(new Date(`${value}T00:00:00`), 'PP') } catch { return value }
    case 'DATETIME':
      try { return format(new Date(value), 'PPp') } catch { return value }
    default:
      return String(value)
  }
}

/** "Sum of Total of Line items (Deal)", "Count of Deals where Stage = open, all levels" */
export const rollupSummary = (field) => {
  const r = field.rollup
  if (!r) return 'roll-up'
  const fn = { COUNT: 'Count', SUM: 'Sum', AVG: 'Average', MIN: 'Lowest', MAX: 'Highest' }[r.fn] || r.fn
  const what = r.fn === 'COUNT' ? '' : ` ${r.field}`
  const type = r.entityType.toLowerCase().replace(/_/g, ' ')
  return `${fn}${what} of linked ${type} records${r.filterField ? ` where ${r.filterField} = ${r.filterValue}` : ''}${r.descendants ? ', all levels' : ''}`
}
